import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, apiErrorText, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import RichTextEditor from "@/components/RichTextEditor";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

export default function Templates() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", type: "contract", content: "" });

  const load = async () => { const { data } = await api.get("/templates"); setRows(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", type: "contract", content: "" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, type: r.type, content: r.content }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/templates/${editing.id}`, form);
      else await api.post("/templates", form);
      toast.success("Modelo salvo"); setOpen(false); load();
    } catch (e) { toast.error(apiErrorText(e)); }
  };
  const remove = async (id) => { if (!window.confirm("Remover modelo?")) return; await api.delete(`/templates/${id}`); toast.success("Removido"); load(); };

  return (
    <Layout
      title="Modelos"
      subtitle="Templates de contratos e propostas com variáveis dinâmicas — ex: {{client_name}}, {{amount}}, {{today_date}}"
      actions={<Button onClick={openNew} data-testid="new-template-btn" className="gap-2"><Plus size={16} weight="bold" /> Novo modelo</Button>}
    >
      <div className="tech-card p-4 mb-6">
        <div className="label-tech text-muted-foreground mb-3">Variáveis disponíveis nos modelos</div>
        <div className="flex flex-wrap gap-2 text-xs mono">
          {["client_name", "client_document", "client_address", "client_email", "client_phone",
            "company_name", "today_date", "inspection_address", "inspection_date",
            "service_description", "amount", "valid_until", "city",
          ].map((v) => <span key={v} className="px-2 py-1 bg-secondary rounded border border-border">{`{{${v}}}`}</span>)}
        </div>
      </div>

      <div className="tech-card overflow-hidden">
        <table className="w-full">
          <thead className="border-b border-border">
            <tr className="text-left">
              <th className="label-tech text-muted-foreground p-4">Nome</th>
              <th className="label-tech text-muted-foreground p-4">Tipo</th>
              <th className="label-tech text-muted-foreground p-4">Atualizado</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody data-testid="templates-list">
            {rows.length === 0 ? (
              <tr><td colSpan={4} className="p-12 text-center text-muted-foreground">Nenhum modelo</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-border hover:bg-secondary/30">
                <td className="p-4 font-bold">{r.name}</td>
                <td className="p-4"><Badge variant="outline">{r.type === "contract" ? "Contrato" : "Proposta"}</Badge></td>
                <td className="p-4 text-sm mono">{formatDate(r.updated_at || r.created_at)}</td>
                <td className="p-4">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-template-${r.id}`}><PencilSimple size={16} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(r.id)}><Trash size={16} className="text-destructive" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar modelo" : "Novo modelo"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label className="label-tech">Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name" />
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="template-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="contract">Contrato</SelectItem>
                  <SelectItem value="proposal">Proposta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label className="label-tech">Conteúdo (use {"{{variavel}}"} para campos dinâmicos)</Label>
              <RichTextEditor value={form.content} onChange={(v) => setForm({ ...form, content: v })} testId="template-editor" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-template-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
